import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "Components/Auth/AuthContext.js";
import { useConfirmedAction } from "Components/hooks/useConfirmedAction.js";
import { useResetStateWithTimeout } from "Components/hooks/useResetStateWithTimeout.js";
import { deleteItem } from "Components/Api/deleteItem.js";
import {
	sendItemToInstagram,
	sendItemToTelegram,
} from "Components/Api/sendItemToSocial.js";
import {
	invalidateItemLists,
	removeItem,
} from "Components/utils/itemCache.js";
import { isValidPostId } from "Components/utils/isValidPostId.js";
import * as Nav from "Components/Navigation/paths.js";

const notificationWindowLifetime = 2000;

/**
 * Действия над вещью на странице базы: удаление, редактирование и публикация
 * в соцсети. Владеет флагами занятости и уведомлением о результате.
 *
 * @param {string} itemId - Id вещи из URL.
 * @returns {{
 *   notification: { isError: boolean, text: string } | null,
 *   actions: Array<{ label: string, onClick: Function, disabled: boolean }>
 * }} `actions` в порядке отображения кнопок; одновременно доступно не более
 *   одного действия.
 */
export const useItemActions = (itemId) => {
	// Действия ходят на сервер по id из URL, поэтому им нужен именно валидный
	// id, а не просто непустой: кодирование пути в Constants.js оставляет ".."
	// нетронутым, и URL-нормализация схлопывает сегмент на родительский путь.
	const hasValidId = isValidPostId(itemId);

	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { checkAuth } = useAuth();
	const [notification, setNotification] = useResetStateWithTimeout(
		null,
		null,
		notificationWindowLifetime
	);

	// Общая обработка провала: на 401 перепроверяем сессию, остальное — в консоль.
	const notifyFailure = async (actionLabel, err) => {
		if (err?.status === 401) {
			await checkAuth();
		} else {
			console.error(`${actionLabel} failed:`, err?.status ?? err);
		}
		setNotification({ isError: true, text: `${actionLabel} failed` });
	};

	// Чистим кеш удалённой вещи не в onSuccess, а в cleanup этого хука.
	//
	// navigate() только ставит смену маршрута в очередь: страница вещи в той
	// же функции ещё смонтирована, useItemDetailsPrivate подписан на ключ.
	// removeItem в этот момент бьёт по активному запросу — observer не узнаёт
	// об удалении записи, а следующий рендер (например setIsBusy(false) в
	// finally useConfirmedAction) пересоздаёт ключ и уходит на сервер за
	// уже удалённым id.
	//
	// Cleanup здесь бежит РАНЬШЕ отписки: React запускает чистки в порядке
	// объявления эффектов, а DatabaseItemPage вызывает useItemDetailsPrivate
	// после этого хука. Безопасно это не поэтому, а потому что между
	// двумя cleanup'ами одного размонтирования React не рендерит: пересоздать
	// запись в кеше и уйти за удалённым id может только рендер. Отсюда же
	// единственный реальный способ сломать — перенести вызов в onSuccess,
	// где рендер будет гарантированно.
	//
	// Флаг нужен, чтобы обычный уход со страницы (назад, каталог) не выбрасывал
	// живую карточку из кеша: pendingRemoveIdRef заполняется только после
	// успешного DELETE. useRef, а не стейт — запись не рендерит, значение
	// доживает до cleanup.
	const pendingRemoveIdRef = useRef(null);
	useEffect(() => {
		return () => {
			if (pendingRemoveIdRef.current == null) return;
			removeItem(queryClient, pendingRemoveIdRef.current);
			pendingRemoveIdRef.current = null;
		};
	}, [queryClient]);

	const deleteAction = useConfirmedAction({
		confirmText: "Delete this item?",
		disabled: !hasValidId,
		request: () => deleteItem({ itemID: itemId }),
		onSuccess: async () => {
			pendingRemoveIdRef.current = itemId;
			await invalidateItemLists(queryClient);
			navigate(`/${Nav.database}`);
		},
		onError: (err) => notifyFailure("Delete", err),
	});

	// Публикация в соцсеть: отличается от удаления только тем, что после успеха
	// показывает уведомление, а не уходит со страницы. Кеш не трогает — статуса
	// публикации в данных вещи нет, см. док-комментарий itemCache.js.
	const notifySent = (channelLabel) =>
		setNotification({ isError: false, text: `posted to ${channelLabel}` });

	const instagramAction = useConfirmedAction({
		confirmText: "Post this item to Instagram?",
		disabled: !hasValidId,
		request: () => sendItemToInstagram({ itemID: itemId }),
		onSuccess: () => notifySent("Instagram"),
		onError: (err) => notifyFailure("Instagram", err),
	});

	const telegramAction = useConfirmedAction({
		confirmText: "Post this item to Telegram?",
		disabled: !hasValidId,
		request: () => sendItemToTelegram({ itemID: itemId }),
		onSuccess: () => notifySent("Telegram"),
		onError: (err) => notifyFailure("Telegram", err),
	});

	const handleEditItem = () => {
		if (!hasValidId) return;
		navigate(Nav.uploadEdit(itemId));
	};

	// Действия взаимоисключающие: пока одно в полёте, остальные заблокированы —
	// иначе можно опубликовать вещь, которая в этот момент удаляется.
	const isAnyBusy =
		deleteAction.isBusy || instagramAction.isBusy || telegramAction.isBusy;

	const actions = [
		{
			label: "Delete",
			onClick: deleteAction.run,
			disabled: deleteAction.disabled || isAnyBusy,
		},
		{
			label: "Edit",
			onClick: handleEditItem,
			disabled: !hasValidId || isAnyBusy,
		},
		{
			label: "Instagram",
			onClick: instagramAction.run,
			disabled: instagramAction.disabled || isAnyBusy,
		},
		{
			label: "Telegram",
			onClick: telegramAction.run,
			disabled: telegramAction.disabled || isAnyBusy,
		},
	];

	return { notification, actions };
};
